import { useCallback, useEffect, useState } from "react";
import { MEETING_REASON_LABEL } from "@/lib/meeting-labels";
import { Link } from "react-router-dom";
import {
  COACH_WELCOME_CTA,
  closeReasonLabel,
  closeReasonsFor,
} from "@care-by-ella/contracts";
import type {
  AiAssistCoachReply as AiAssistCoachReplyDto,
  DisposeWorkItemRequest,
  FollowUpOwnerRole,
  FollowUpContactContext,
  Role,
  SetFollowUpPolicyInput,
  WorkItemDisposition,
  WorkQueueItem,
  WorkQueueItemContext,
  WorkQueueItemKind,
  WorkQueuePriority,
} from "@care-by-ella/contracts";
import { MEDICAL_REVIEW_NEUTRAL_STATUS } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { DISPOSITION_LABEL } from "@/lib/coach-history";
import { CARD_NO_CHANGE_FALLBACK, cardChangeLine } from "@/lib/coach-now";
import { PatientLabel } from "@/components/PatientLabel";
import {
  CloseReasonPicker,
  EMPTY_CLOSE_REASON,
  closeReasonBody,
  closeReasonReady,
  type CloseReasonValue,
} from "@/components/CloseReasonPicker";
import {
  AiAssistDisabledError,
  checkinActionErrorText,
  closeCheckinWithoutReply,
  ForbiddenError,
  fetchAiCoachReply,
  generateAiCoachReply,
  markAiDraftUsed,
  sendCheckinCoachReply,
  sendStaffChatMessage,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { createHandoff } from "@/lib/handoff-api";
import { confirmWebdocEntry, setFollowUpPolicy } from "@/lib/work-queue-api";
import {
  createCoachFollowUp,
  disposeWorkItem,
  fetchWorkItemContext,
} from "@/lib/work-queue-api";
import { useReadOnly, useViewPath } from "./SupportView";
import { UnassignedCoachActions } from "./CoachAssignment";
import { LabResultCheckPanel } from "./LabResultCheckPanel";
import { AiCoachDraftAssist, reportCoachDraftSent } from "./AiCoachDraftAssist";
import { appendToComposer, removeDraftParts } from "@/lib/coach-draft-composer";
import { FollowUpLaterFields } from "./FollowUpLaterFields";
import { AdminCloseWorkItem } from "./AdminCloseWorkItem";
import {
  EMPTY_FOLLOW_UP_LATER,
  followUpLaterPayload,
  followUpLaterProblem,
  type FollowUpLaterValue,
} from "@/lib/follow-up-later";
import {
  PATIENT_OVERVIEW_LABEL,
  patientOverviewHref,
  patientOverviewLinkFor,
  workItemPrimaryCta,
} from "@/lib/work-item-cta";

// TES-757 — the expanded work-queue card.
//
// The row used to be a pure pointer ("acts on nothing itself"). It still is
// when collapsed; expanding it fetches the context and offers the six actions
// the coach-admin package specifies, so a check-in can be answered and closed
// without leaving the queue.
//
// Two rules this component exists to keep:
//
//  1. THE AI DRAFT IS A FIELD, NOT THE FLOW. `AI_ASSIST_ENABLED` is
//     environment-scoped and off in production pending Ö-147/Ö-148. Every
//     action below works with the draft area absent — it hides itself on
//     `ai_assist_disabled` exactly like AiAssistCoachReply does. If the card
//     required a draft, the whole block would be stuck behind legal.
//
//  2. A MEDICAL SIGNAL IS NOT AN ORDINARY COACH CASE. A doctor-routed check-in
//     gets no draft at all (the server refuses), and the card drops the coach
//     reply entirely rather than offering it alongside an escalation — it
//     becomes the `alert` class below. The routing decision stays on the
//     server; this only reflects it.
//
//  3. THE ACTION ROW IS TYPED, AND KNOWS WHO IS LOOKING (TES-813). See the
//     table below: every button a card offers comes out of `actionsFor`, so
//     the row cannot drift back into the shared four-button strip that put
//     "Skicka till läkare" in front of a doctor.

const PRIORITY_LABEL: Record<WorkQueuePriority, string> = {
  low: "Låg",
  medium: "Medel",
  high: "Hög",
  urgent: "Akut",
};

const PRIORITY_CLASS: Record<WorkQueuePriority, string> = {
  low: "bg-surfaceMuted text-textSecondary",
  medium: "bg-brand/10 text-brand",
  high: "bg-warning/15 text-warning",
  urgent: "bg-danger/10 text-danger",
};

const BTN =
  "rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90 disabled:opacity-50";
const TEXTAREA =
  "w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

// ---------------------------------------------------------------------------
// TES-813 — what a card offers, and to whom
// ---------------------------------------------------------------------------
//
// Emelie 2026-09-19, reviewing the doctor's lanes in QA:
// "Generiska CTA:er som 'Skicka till läkare' visas även när läkaren själv hanterar ärendet."
// "Beslutsärenden, medicinska larm och administrativa kvittenser behöver tydligare åtskillnad."
//
// The row used to sit OUTSIDE every `item.kind` branch, so a lab order, a
// Webdoc receipt and a medical alarm were each handed the same four buttons —
// and a doctor working her own Blodprov queue was invited to escalate the case
// to herself. Three classes, because they are three different jobs:
//
//   decision   — "receptövertag", monthly check-in, lab results. Decide.
//   alert      — a medical alarm, or a check-in the server routed to a doctor.
//                Acknowledge or escalate; never answered as coach mail.
//   admin_ack  — the Webdoc receipt. Confirm. Nothing to answer, nobody to
//                escalate to, nothing to follow up.
//   review     — TES-986: a medical review case. Only "open": it is handled
//                on the patient's Veckoincheckningar tab, by opening the
//                originals and marking it granskad — never closed from here.
//   welcome    — TES-1189: "Välkomna ny patient". Its one action opens the
//                coach chat with the welcome draft, and the case closes when
//                the coach has sent her first message — never from here.
//   meeting    — TES-1233: a passed meeting without documentation or active
//                handling. Only "open": the row is a projection that leaves
//                once the meeting is documented, so a disposition would hide
//                the row while the meeting stayed unwritten.
type WorkItemClass =
  | "decision"
  | "alert"
  | "admin_ack"
  | "review"
  | "welcome"
  | "meeting";

type WorkItemAction =
  | "confirm"
  | "reply"
  | "escalate"
  | "follow_up"
  | "open"
  | "close";

const CLASS_OF_KIND: Record<WorkQueueItemKind, WorkItemClass> = {
  handoff: "decision",
  // TES-915 — her own reminder: act on it, set a new date, or close it.
  follow_up: "decision",
  checkin: "decision",
  lab_order: "decision",
  missed_check_in: "decision",
  missed_weight_log: "decision",
  needs_follow_up_contact: "decision",
  coach_welcome: "welcome",
  program_change: "decision",
  renewal: "decision",
  onboarding: "decision",
  side_effect: "decision",
  medical_alert: "alert",
  medical_review: "review",
  webdoc_confirmation: "admin_ack",
  meeting_documentation: "meeting",
  // TES-1059 — every item of a completion round is in: open the patient and
  // decide; closing the card marks the round reviewed.
  completion_round: "decision",
  // TES-1149 — she answered his question. TES-1332: rendered by
  // DoctorAnswerCard (WorkQueueRow), which handles it in the row.
  doctor_question_answer: "decision",
  // TES-1203 — she withdrew in the app: open her overview, then close.
  case_withdrawal: "decision",
  // TES-1554 — admin's end-and-refund task. Listed on "Avslut och
  // återbetalning", never in a clinician's lane.
  subscription_end_refund: "admin_ack",
};

// Ordered lead-first: the primary button is the first entry that survives the
// filters in `actionsFor`, so a class never ends up without a lead because a
// role filter removed the one it was written around.
//
// `open` outranks `escalate` and `follow_up` in `decision` on purpose. A
// renewal, a lab order or an onboarding row has no composer, so the moment
// `reply` is filtered out the lead has to be "go where the decision is made" —
// `ctaPath` already deep-links to the right tab. That is this component's
// oldest rule, from the header: the workspace surfaces, the owning surface
// acts. Without the ordering a doctor's renewal card led with
// "Följ upp senare", which is a way of not deciding.
//
// `alert` deliberately has no `follow_up`. That button books a COACH follow-up
// (sourceType `coach_note`); parking a medical alarm in the coach's notebook
// is not one of the two moves the package allows here. `close` stays —
// "sett, inget behövs" with its mandatory reason is a doctor's answer, and
// without it an alarm has no terminal state inside the card.
//
// `admin_ack` deliberately has no `close`. The Webdoc section below already
// argues why: the row is a PROJECTION that dies of the confirmation existing,
// so a disposition would hide the row while the task itself remained.
const CLASS_ACTIONS: Record<WorkItemClass, readonly WorkItemAction[]> = {
  decision: ["reply", "open", "escalate", "follow_up", "close"],
  alert: ["escalate", "open", "close"],
  admin_ack: ["confirm", "open"],
  review: ["open"],
  welcome: ["open"],
  meeting: ["open"],
};

// Only two kinds give anyone somewhere to type: a check-in (the AI draft, or a
// plain reply when the draft is off) and "behöver återkontakt" (Emelies warm
// question). On every other kind "Skicka och avsluta" rendered a PRIMARY
// button that could never enable itself, because `text` stays empty with no
// composer above it — the generic CTA in its purest form.
const KINDS_WITH_COMPOSER: readonly WorkQueueItemKind[] = [
  "checkin",
  "needs_follow_up_contact",
];

// Emelies third requirement: the three classes have to be tellable apart
// BEFORE the card is expanded. Outlined rather than filled so the chip does
// not read as a second priority pill.
const CLASS_CHIP: Record<WorkItemClass, { label: string; className: string }> =
  {
    decision: { label: "Beslut", className: "border-brand/40 text-brand" },
    alert: { label: "Larm", className: "border-danger/50 text-danger" },
    admin_ack: {
      label: "Kvittens",
      className: "border-surfaceMuted text-textSecondary",
    },
    review: {
      label: "Medicinskt ärende",
      className: "border-danger/50 text-danger",
    },
    welcome: { label: "Ny patient", className: "border-brand/40 text-brand" },
    meeting: {
      label: "Vårdmöte",
      className: "border-warning text-textPrimary",
    },
  };

// The class is a property of the kind, with one runtime upgrade: a check-in
// the server routed to a doctor is a medical signal wearing a check-in's
// clothes. That routing only becomes knowable once the card is OPEN — it
// arrives as the refusal reason on the AI draft — so a collapsed check-in
// still reads "Beslut" and turns into "Larm" when it is expanded.
function workItemClass(
  kind: WorkQueueItemKind,
  doctorRouted = false,
): WorkItemClass {
  return doctorRouted ? "alert" : CLASS_OF_KIND[kind];
}

// The one place the rule lives. Two subtractions, each with a reason:
//
//  - a DOCTOR is never offered `escalate`. `createHandoff` addresses the
//    handoff `toRole: "doctor"`, so the escalation would have no recipient
//    other than the person already looking at it. That is the finding.
//  - a kind with no composer is never offered `reply`. See above.
//
// The server's WorkItemActionService still owns the routing decision and the
// disposition rules; this only stops offering what cannot mean anything.
function actionsFor(
  kind: WorkQueueItemKind,
  role: Role | undefined,
  doctorRouted = false,
): readonly WorkItemAction[] {
  return CLASS_ACTIONS[workItemClass(kind, doctorRouted)].filter((action) => {
    if (action === "escalate") return role !== "doctor";
    if (action === "reply") return KINDS_WITH_COMPOSER.includes(kind);
    return true;
  });
}

// The refusals after which a check-in card has no AI draft and keeps a plain
// reply box: AI assist off, a doctor looking (the route is coach-only), or —
// TES-1561 — a monthly check-in, which the draft route does not serve.
function draftUnavailable(e: unknown): boolean {
  return (
    e instanceof AiAssistDisabledError ||
    e instanceof ForbiddenError ||
    (e instanceof Error && e.message === "ai_assist_checkin_not_found")
  );
}

// TES-751 adds "pause" and "interval" — the anti-tjat controls.
type Busy =
  | "draft"
  | "send"
  | "close"
  | "escalate"
  | "follow_up"
  | "pause"
  | "interval"
  | "webdoc"
  | null;

export function WorkItemCard({
  item,
  autoExpand = false,
  onDisposed,
  onUnauthorized,
}: {
  item: WorkQueueItem;
  // TES-757 — the row the server handed back as "next" opens by itself after
  // the list refreshes. That is what makes "skicka och avsluta" a loop the
  // coach stays inside: without it the next item is returned by the API and
  // then thrown away, and she is back to scanning a list.
  autoExpand?: boolean;
  onDisposed?: (next: WorkQueueItem | null) => void;
  onUnauthorized?: () => void;
}) {
  // TES-813 — the card has to know WHO is looking, not just what the row is.
  // A doctor in her own lane must not be offered an escalation to herself.
  const { claims } = useAuth();
  // TES-1040 — in the admin's read-only support view the card is a row with
  // a link to the patient: "Hantera" opens the item's context (not opened to
  // an admin) and every action under it is a write.
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<WorkQueueItemContext | null>(null);
  const [draft, setDraft] = useState<AiAssistCoachReplyDto | null>(null);
  const [draftHidden, setDraftHidden] = useState(false);
  const [text, setText] = useState("");
  // TES-1237 — the AI suggestion currently in the composer (QA only).
  const [aiDraft, setAiDraft] = useState<{
    id: string;
    original: string;
  } | null>(null);
  const [reason, setReason] = useState("");
  // TES-1208 — a coach closes with A-01's structured reason; a doctor's
  // card keeps the free-text reason above (stored as `other`).
  const [closeReason, setCloseReason] =
    useState<CloseReasonValue>(EMPTY_CLOSE_REASON);
  const [askingReason, setAskingReason] = useState(false);
  // TES-1400 (Ö-426) — owner, date and time, lane and a visible reason.
  const [followUp, setFollowUp] = useState<FollowUpLaterValue>(
    EMPTY_FOLLOW_UP_LATER,
  );
  const [askingFollowUp, setAskingFollowUp] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<WorkItemDisposition | null>(null);
  // TES-899 — the server found the check-in already handled (another view,
  // a reload) and returned that result: nothing new went to the patient.
  const [replayed, setReplayed] = useState(false);

  const fail = useCallback(
    (e: unknown, fallback: string) => {
      if (e instanceof UnauthorizedError) onUnauthorized?.();
      else setError(fallback);
    },
    [onUnauthorized],
  );

  // Only a weekly check-in has a draft today: the AI-assist endpoint is keyed
  // on the submission, and for a `checkin` row the item id IS that submission.
  // Generalising the draft to every kind is its own slice (B2).
  const draftable = item.kind === "checkin";
  // TES-899 — a coach's check-in is finished through its own two actions,
  // which link the reply to the check-in and refuse to send it twice. A
  // doctor's check-in card keeps the generic close.
  const coachCheckin = item.kind === "checkin" && claims?.role === "coach";
  // TES-1208 — A-01 is the coach's document: her closes carry its
  // structured reasons. The API refuses a coach's close without one.
  const structuredClose = claims?.role === "coach";
  const closeReady = structuredClose
    ? closeReasonReady(closeReason)
    : reason.trim().length > 0;
  // A-01's names for the secondary close: "Avsluta utan svar" on a
  // check-in, "Avsluta utan kontakt" on "Behöver återkontakt", otherwise
  // "Avsluta utan åtgärd".
  const closeLabel = coachCheckin
    ? "Avsluta utan svar"
    : item.kind === "needs_follow_up_contact"
      ? "Avsluta utan kontakt"
      : "Avsluta utan åtgärd";

  const expand = useCallback(async () => {
    setOpen(true);
    if (context) return;
    try {
      setContext(await fetchWorkItemContext(item.kind, item.id));
    } catch (e) {
      fail(e, "Kunde inte hämta ärendet.");
      return;
    }
    // TES-751 — Emelie's suggested warm contact, verbatim, as the starting text.
    // It is deliberately written so that it does not demand an explanation from
    // the patient; the whole point of the section is to reach out, not to
    // extract an answer. The coach can rewrite it — it is a draft, not a
    // template.
    if (item.kind === "needs_follow_up_contact") {
      setText(
        (current) =>
          current ||
          "Hej! Jag ville bara höra hur du har det. Du behöver inte förklara varför incheckningarna inte blivit av – säg gärna till om det finns något vi kan göra för att stödet ska passa dig bättre.",
      );
    }
    if (!draftable) return;
    try {
      const d = await fetchAiCoachReply(item.patientId, item.id);
      setDraft(d);
      if (d.reply) setText(d.reply);
    } catch (e) {
      // The draft is optional by design. Two ways it legitimately is not
      // there, and both hide the area rather than leaving a dead textarea:
      //   - AI assist is off for the environment (Ö-147/Ö-148 still open)
      //   - the caller is a DOCTOR. The coach-reply route is @Roles("coach"),
      //     and this row is shared with the doctor's lane pages, so a 403 here
      //     is an ordinary fact about who is looking — not an error.
      //   - TES-1561 — a monthly check-in (F-05): drafts are written for
      //     the weekly one only, so the coach writes her reply herself.
      if (draftUnavailable(e)) {
        setDraftHidden(true);
      } else if (e instanceof UnauthorizedError) onUnauthorized?.();
    }
  }, [context, draftable, fail, item, onUnauthorized]);

  // The row the server named as "next" opens itself once the refreshed list
  // has rendered it. Guarded on `open` so it runs once, not on every context
  // change.
  useEffect(() => {
    if (autoExpand && !open && !readOnly) void expand();
  }, [autoExpand, open, expand, readOnly]);

  async function generate() {
    setBusy("draft");
    setError(null);
    try {
      const d = await generateAiCoachReply(item.patientId, item.id);
      setDraft(d);
      if (d.reply) setText(d.reply);
    } catch (e) {
      if (draftUnavailable(e)) {
        setDraftHidden(true);
      } else fail(e, "Kunde inte ta fram ett utkast.");
    } finally {
      setBusy(null);
    }
  }

  async function finish(
    disposition: DisposeWorkItemRequest["disposition"],
    body?: Pick<DisposeWorkItemRequest, "reason" | "reasonCode">,
  ) {
    const res = await disposeWorkItem(item.kind, item.id, {
      disposition,
      ...body,
    });
    setDone(disposition);
    onDisposed?.(res.next);
  }

  async function sendAndClose() {
    const body = text.trim();
    if (!body) return;
    setBusy("send");
    setError(null);
    if (coachCheckin) {
      // "Skicka svar och slutför": one request sends, links, completes and
      // takes the row off the queue. A repeat returns the first result.
      try {
        const res = await sendCheckinCoachReply(item.patientId, item.id, {
          body,
          ...(draft?.draft ? { aiDraftId: draft.draft.id } : {}),
        });
        setReplayed(res.replayed);
        setDone("closed_with_reply");
        onDisposed?.(res.next);
      } catch (e) {
        fail(e, checkinActionErrorText(e));
      } finally {
        setBusy(null);
      }
      return;
    }
    try {
      await sendStaffChatMessage(item.patientId, "coach", body);
      if (aiDraft) reportCoachDraftSent(item.patientId, aiDraft, body);
      if (draft?.draft) {
        void markAiDraftUsed(
          item.patientId,
          draft.draft.id,
          "coachReply",
        ).catch(() => undefined);
      }
      await finish("closed_with_reply");
    } catch (e) {
      fail(e, "Svaret kunde inte skickas. Ärendet är kvar i kön.");
    } finally {
      setBusy(null);
    }
  }

  async function closeWithoutAction() {
    if (!closeReady) return;
    setBusy("close");
    setError(null);
    if (coachCheckin) {
      // "Avsluta utan svar" — the check-in's own state, with its reason.
      try {
        const res = await closeCheckinWithoutReply(
          item.patientId,
          item.id,
          closeReasonBody(closeReason),
        );
        setReplayed(res.replayed);
        setDone("closed_without_action");
        onDisposed?.(res.next);
      } catch (e) {
        fail(e, checkinActionErrorText(e));
      } finally {
        setBusy(null);
      }
      return;
    }
    try {
      await finish(
        "closed_without_action",
        structuredClose
          ? closeReasonBody(closeReason)
          : { reason: reason.trim() },
      );
    } catch (e) {
      fail(e, "Ärendet kunde inte avslutas.");
    } finally {
      setBusy(null);
    }
  }

  // TES-751 — "coachen kan pausa nya påminnelser i fyra veckor" and
  // "registrera överenskommen kontaktfrekvens". Neither closes the card: a
  // pause is about the NEXT one, and an agreed frequency is about every one
  // after that. The coach still has to say what she did with this one.
  async function applyPolicy(
    body: SetFollowUpPolicyInput,
    busyKey: Extract<Busy, "pause" | "interval">,
  ) {
    setBusy(busyKey);
    setError(null);
    try {
      const next = await setFollowUpPolicy(item.patientId, body);
      setContext((c) =>
        c
          ? {
              ...c,
              followUpContact: c.followUpContact
                ? { ...c.followUpContact, policy: next }
                : null,
            }
          : c,
      );
    } catch (e) {
      fail(e, "Inställningen kunde inte sparas.");
    } finally {
      setBusy(null);
    }
  }

  // TES-765 — confirm that the meeting is entered in Webdoc. The card
  // disappears because the projection no longer matches, not because we
  // marked it.
  async function confirmWebdoc() {
    setBusy("webdoc");
    setError(null);
    try {
      await confirmWebdocEntry(item.patientId, item.id);
      setDone("closed_with_reply");
      onDisposed?.(null);
    } catch (e) {
      fail(e, "Bekräftelsen kunde inte sparas.");
    } finally {
      setBusy(null);
    }
  }

  async function escalate() {
    setBusy("escalate");
    setError(null);
    try {
      await createHandoff(item.patientId, {
        toRole: "doctor",
        reason: item.title,
        priority: item.priority === "urgent" ? "urgent" : "high",
      });
      await finish("escalated");
    } catch (e) {
      fail(e, "Överlämningen kunde inte skapas.");
    } finally {
      setBusy(null);
    }
  }

  const myRole: FollowUpOwnerRole =
    claims?.role === "coach" ? "coach" : "doctor";
  const followUpProblem = followUpLaterProblem(followUp, myRole);

  async function scheduleFollowUp() {
    if (followUpProblem || !claims?.sub) return;
    setBusy("follow_up");
    setError(null);
    try {
      await createCoachFollowUp(
        item.patientId,
        followUpLaterPayload(followUp, claims.sub, { itemId: item.id }),
      );
      await finish("deferred");
    } catch (e) {
      fail(e, "Uppföljningen kunde inte sparas.");
    } finally {
      setBusy(null);
    }
  }

  // A doctor-routed check-in is the server's decision, surfaced here: the
  // package forbids resolving a medical signal as an ordinary coach case.
  // `blocked` carries the REASON the server refused a draft (a doctor-routed
  // check-in), not a boolean — so read it as presence, not equality.
  const medical = draft?.blocked != null;

  // TES-813 — every button below comes out of these three lines. `lead` is the
  // first surviving action, so removing `escalate` for a doctor promotes the
  // next one rather than leaving an alarm card with no primary at all.
  const itemClass = workItemClass(item.kind, medical);
  // TES-1579 — a non-meeting journal note's Webdoc row has no inline
  // confirmation: its tick belongs to the note's own step, which the
  // primary link opens.
  const actions = actionsFor(item.kind, claims?.role, medical).filter(
    (a) => !(a === "confirm" && item.journalParentKind),
  );
  const chip = CLASS_CHIP[itemClass];
  // TES-1245 (A-02) — "open" is no longer an inline button: the header's
  // primary action IS the link to the work step, and a second link to the
  // same `ctaPath` under it was exactly the duplicate A-02 rules out. When
  // "open" was the class's lead, nothing inline is styled as primary.
  const inlineActions = actions.filter((a) => a !== "open");
  const lead = actions[0] === "open" ? null : actions[0];
  const primary = workItemPrimaryCta(item, claims?.role);
  const overviewHref = patientOverviewLinkFor(item.patientId, primary);

  if (done) {
    return (
      <div className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-md text-sm text-textSecondary">
        {replayed
          ? "Incheckningen var redan hanterad. Inget nytt svar skickades."
          : done === "closed_with_reply"
            ? coachCheckin
              ? "Svar skickat och incheckningen slutförd."
              : "Svar skickat och ärendet avslutat."
            : done === "closed_without_action" && coachCheckin
              ? "Incheckningen avslutad utan svar."
              : done === "escalated"
                ? "Skickat till läkare."
                : done === "deferred"
                  ? "Uppföljning inlagd."
                  : "Ärendet avslutat utan åtgärd."}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-surfaceMuted bg-surface">
      <div className="flex flex-wrap items-center gap-sm p-md">
        <span
          className={`rounded-pill px-sm py-xs text-xs ${PRIORITY_CLASS[item.priority]}`}
        >
          {PRIORITY_LABEL[item.priority]}
        </span>
        {/* TES-813 — which of the three jobs this row is, readable without
            expanding it. Collapsed we only know the kind, so a doctor-routed
            check-in reads "Beslut" here and becomes "Larm" once the card is
            open and the routing is known. */}
        <span
          className={`rounded-pill border px-sm py-xs text-xs ${chip.className}`}
        >
          {chip.label}
        </span>
        <span className="text-sm text-textPrimary">{item.title}</span>
        {/* TES-676 — an alarm nobody has confirmed seeing must look like one. */}
        {item.kind === "medical_alert" && item.acknowledgedAt === null ? (
          <span className="rounded-pill bg-danger px-sm py-xs text-xs text-white">
            Ej mottaget
          </span>
        ) : null}
        <PatientLabel
          className="text-sm"
          patientId={item.patientId}
          name={item.patientName}
          personalNumberLast6={item.personalNumberLast6}
          link
        />
        {item.dueAt ? (
          <span className="text-xs text-textSecondary">
            senast {item.dueAt.slice(0, 10)}
          </span>
        ) : null}
        {/* TES-892 (Ö-216) — 24 staffed weekday hours without anything happening.
            The server decides it: only it knows the tenant's staffed hours,
            and a wall-clock reading in the browser would light this up over
            every weekend. `medical_alerts` rows never carry it — that lane is
            already urgent and keeps its own priority track. */}
        {item.delayed ? (
          <span
            className="rounded-pill border border-danger/40 bg-danger/10 px-sm py-xs text-xs text-danger"
            title="Mer än 24 bemannade vardagstimmar utan att något hänt i ärendet"
          >
            Försenad
          </span>
        ) : null}
        {item.waitingSince ? (
          <span className="text-xs text-textSecondary">
            väntat sedan {item.waitingSince.slice(0, 10)}
          </span>
        ) : null}
        {/* TES-1189 — the welcome case has nothing to handle inside the
            card: its primary action IS the row, straight into the coach chat
            with the draft. The admin's read-only view keeps the plain link. */}
        {/* TES-1218 (Ö-407) — the shared "Ny patient saknar coach" case:
            "Tilldela mig" for a coach, "Tilldela coach" for an admin (also
            in her read-only coach view: assigning is an admin action, not a
            clinician's write). Either one turns it into that coach's
            welcome case, so the queue reloads. */}
        {item.kind === "coach_welcome" && item.needsCoach ? (
          <div className="ml-auto flex flex-wrap items-center gap-sm">
            <UnassignedCoachActions
              patientId={item.patientId}
              canClaim={claims?.role === "coach" && !readOnly}
              onAssigned={() => onDisposed?.(null)}
              onUnauthorized={onUnauthorized}
            />
            <Link
              to={viewPath(patientOverviewHref(item.patientId))}
              className={BTN}
            >
              {PATIENT_OVERVIEW_LABEL}
            </Link>
          </div>
        ) : item.kind === "coach_welcome" && !readOnly ? (
          <div className="ml-auto flex items-center gap-sm">
            <Link to={viewPath(item.ctaPath)} className={BTN_PRIMARY}>
              {COACH_WELCOME_CTA}
            </Link>
          </div>
        ) : (
          // TES-1245 (A-02) — one concrete primary that opens the work step
          // (in the card, or on the page that performs it), and
          // "Öppna patientbilden" beside it only when that is another view.
          // A step decided on the page keeps the card's own actions (close,
          // follow-up, escalate) behind "Fler val".
          <div className="ml-auto flex flex-wrap items-center gap-sm">
            {primary.how === "link" ? (
              <Link to={viewPath(primary.href)} className={BTN_PRIMARY}>
                {primary.label}
              </Link>
            ) : readOnly ? null : (
              <button
                type="button"
                onClick={() => (open ? setOpen(false) : void expand())}
                className={BTN_PRIMARY}
                aria-expanded={open}
              >
                {open ? "Dölj" : primary.label}
              </button>
            )}
            {overviewHref ? (
              <Link to={viewPath(overviewHref)} className={BTN}>
                {PATIENT_OVERVIEW_LABEL}
              </Link>
            ) : null}
            {/* TES-1400 (Ö-410) — "Stäng arbetsmoment", for an admin only
                (also in her read-only view: closing an administrative item
                is an admin action, not a clinician's write), and only on
                the administrative kinds. */}
            {item.kind === "webdoc_confirmation" ? (
              <AdminCloseWorkItem
                kind="webdoc_confirmation"
                itemId={item.id}
                onClosed={() => onDisposed?.(null)}
                onUnauthorized={onUnauthorized}
              />
            ) : null}
            {primary.how === "link" && !readOnly && inlineActions.length > 0 ? (
              <button
                type="button"
                onClick={() => (open ? setOpen(false) : void expand())}
                className={BTN}
                aria-expanded={open}
              >
                {open ? "Dölj" : "Fler val"}
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* TES-1284 — the Webdoc check's facts and three choices, on the row
          itself. A choice reloads the list: the same item comes back in its
          new state (waiting, escalated) or leaves for the completion round. */}
      {item.kind === "lab_order" && item.labResultCheck ? (
        <LabResultCheckPanel
          patientId={item.patientId}
          orderId={item.id}
          check={item.labResultCheck}
          onRecorded={() => onDisposed?.(null)}
          onUnauthorized={onUnauthorized}
        />
      ) : null}

      {open && !readOnly ? (
        <div className="border-t border-surfaceMuted p-md">
          {error ? <p className="mb-sm text-sm text-danger">{error}</p> : null}

          {!context && !error ? (
            <p className="text-sm text-textSecondary">Hämtar ärendet…</p>
          ) : !context ? null : (
            <div className="flex flex-col gap-md">
              {context.disposition ? (
                <p className="text-sm text-textSecondary">
                  Redan hanterat {context.disposition.handledAt.slice(0, 10)}.
                </p>
              ) : null}

              {item.kind === "webdoc_confirmation" ? (
                // TES-765 (CP-05) — "Infört i Webdoc".
                //
                // No disposition is written here. The row is a PROJECTION of
                // "dokumenterat möte utan bekräftelse", so it goes out because
                // the confirmation exists — not because someone marks the card
                // as handled. A close without a confirmation would have hidden
                // the row while the task remained.
                //
                // TES-813 — the button itself moved into the action row below,
                // where it is `admin_ack`'s lead. It used to live here while a
                // second, generic row underneath offered a reply, an
                // escalation and a follow-up on the same card.
                <Section title="Infört i Webdoc">
                  {/* TES-812 — WHAT is being confirmed. Emelie could not tell
                      which consult a row referred to, and the confirmation is
                      an assertion about a specific note: "this one is in
                      Webdoc". Asserting it without seeing it is how the wrong
                      note gets ticked. The excerpt is the note's first line;
                      the record itself is behind the CTA. */}
                  {item.meetingReason || item.noteExcerpt ? (
                    <div className="mb-sm rounded-sm border border-surfaceMuted bg-surfaceMuted/30 p-sm">
                      {item.meetingReason ? (
                        <p className="text-xs text-textSecondary">
                          {MEETING_REASON_LABEL[item.meetingReason] ??
                            item.meetingReason}
                        </p>
                      ) : null}
                      {item.noteExcerpt ? (
                        <p className="mt-xs text-sm text-textPrimary italic">
                          &ldquo;{item.noteExcerpt}&rdquo;
                        </p>
                      ) : (
                        <p className="mt-xs text-sm text-textSecondary">
                          Anteckningen saknar bedömningstext — mötet
                          dokumenterades med nästa steg men utan fritext.
                        </p>
                      )}
                    </div>
                  ) : null}
                  <p className="text-sm text-textSecondary">
                    Patientens vård har redan gått vidare — bekräftelsen håller
                    ingenting tillbaka. Det här är den administrativa svansen.
                  </p>
                  {/* TES-1245 — the whole note is in the meeting; the header
                      no longer carries a generic link there. */}
                  <Link
                    to={viewPath(item.ctaPath)}
                    className="mt-xs inline-block text-sm text-brand underline"
                  >
                    Visa journalanteckningen
                  </Link>
                </Section>
              ) : null}

              {context.followUpContact ? (
                <FollowUpContactPanel
                  card={context.followUpContact}
                  patientId={item.patientId}
                  busy={busy}
                  onPolicy={applyPolicy}
                />
              ) : null}

              <Section title="Förändring sedan senaste kontakten">
                {/* TES-1214 — A-01: the card may not say "Inget noterat
                    sedan sist" while there is history without saying why.
                    For the coach it says since when nothing is new (or that
                    she has not written yet), in Aktuellt's words. */}
                {context.changesSinceLastContact.length === 0 ? (
                  <Empty>
                    {claims?.role === "coach"
                      ? cardChangeLine(context.changesSince)
                      : CARD_NO_CHANGE_FALLBACK}
                  </Empty>
                ) : (
                  <Bullets
                    items={context.changesSinceLastContact.map((c) => ({
                      id: c.id,
                      body: c.body,
                      meta: c.observedAt.slice(0, 10),
                    }))}
                  />
                )}
              </Section>

              <Section title="Öppen överenskommelse">
                {context.openAgreements.length === 0 ? (
                  <Empty>Ingen överenskommelse registrerad.</Empty>
                ) : (
                  <Bullets
                    items={context.openAgreements.map((a) => ({
                      id: a.id,
                      body: a.body,
                      meta: a.observedAt.slice(0, 10),
                    }))}
                  />
                )}
              </Section>

              {context.openFollowUps.length > 0 ? (
                <Section title="Redan inlagda uppföljningar">
                  <Bullets
                    items={context.openFollowUps.map((f) => ({
                      id: f.id,
                      body: f.prompt,
                      meta: f.dueAt.slice(0, 10),
                    }))}
                  />
                </Section>
              ) : null}

              {medical ? (
                <div className="rounded-md border border-warning/30 bg-warning/10 p-sm text-sm text-textPrimary">
                  {MEDICAL_REVIEW_NEUTRAL_STATUS}
                </div>
              ) : null}

              {/* TES-813 — the composer and the send button are now gated on
                  the SAME fact. They used to disagree: a lab order rendered no
                  textarea and still offered "Skicka och avsluta", permanently
                  disabled because there was nothing to type into. */}
              {!actions.includes("reply") ? null : draftable && !draftHidden ? (
                <Section title="AI-utkast – inte skickat">
                  {draft?.draft ? (
                    <p className="mb-xs text-xs text-textSecondary">
                      Speglar bara det patienten skrev. Råd och egna ord lägger
                      du till själv innan du skickar.
                    </p>
                  ) : null}
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={5}
                    className={TEXTAREA}
                    aria-label="Svarsutkast"
                    placeholder="Skriv ditt svar, eller ta fram ett utkast."
                  />
                  <button
                    type="button"
                    onClick={() => void generate()}
                    disabled={busy !== null}
                    className={`mt-xs ${BTN}`}
                  >
                    {busy === "draft" ? "Tar fram…" : "Ta fram utkast"}
                  </button>
                </Section>
              ) : draftable ? (
                <Section title="Svar">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={5}
                    className={TEXTAREA}
                    aria-label="Svar"
                    placeholder="Skriv ditt svar."
                  />
                </Section>
              ) : (
                // TES-751 — "Skicka en varm fråga". Prefilled with Emelies
                // formulering and editable: it is a starting point, not a
                // template. TES-1237 — in QA the coach may also ask for an AI
                // suggestion under the narrowed contract: no patient text
                // exists here, so the model gets only the session id and the
                // selected Patientminne rows.
                <Section title="Varm fråga">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={4}
                    className={TEXTAREA}
                    aria-label="Varm fråga"
                    placeholder="Skriv din fråga."
                  />
                  {claims?.role === "coach" &&
                  item.kind === "needs_follow_up_contact" ? (
                    <AiCoachDraftAssist
                      patientId={item.patientId}
                      caseKind="recontact"
                      buttonLabel="Föreslå ett annat meddelande"
                      activeDraftId={aiDraft?.id ?? null}
                      onDraft={(d) => {
                        setAiDraft(d);
                        setText(d.original);
                      }}
                      onAppend={(t) => setText((c) => appendToComposer(c, t))}
                      onDiscard={(parts) => {
                        setAiDraft(null);
                        setText((c) => removeDraftParts(c, parts));
                      }}
                      onUnauthorized={() => onUnauthorized?.()}
                    />
                  ) : null}
                </Section>
              )}

              {askingReason ? (
                <Section
                  title={
                    coachCheckin
                      ? "Varför behövs inget svar?"
                      : item.kind === "needs_follow_up_contact"
                        ? "Varför behövs ingen kontakt?"
                        : "Varför behövs ingen åtgärd?"
                  }
                >
                  {structuredClose ? (
                    <CloseReasonPicker
                      name={`close-reason-${item.kind}-${item.id}`}
                      options={closeReasonsFor(item.kind)}
                      value={closeReason}
                      onChange={setCloseReason}
                      disabled={busy !== null}
                    />
                  ) : (
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      className={TEXTAREA}
                      aria-label="Orsak"
                      placeholder="Kort motivering — obligatorisk."
                    />
                  )}
                </Section>
              ) : null}

              {askingFollowUp ? (
                <Section title="Följ upp senare">
                  <FollowUpLaterFields
                    idPrefix={`follow-up-${item.id}`}
                    value={followUp}
                    onChange={setFollowUp}
                    myRole={myRole}
                    problem={followUpProblem}
                    disabled={busy !== null}
                  />
                </Section>
              ) : null}

              {/* TES-813 — the row renders the TABLE, not a hand-rolled set of
                  conditions. Adding a work-item kind now means adding a line to
                  CLASS_OF_KIND; it cannot accidentally inherit four buttons. */}
              <div className="flex flex-wrap items-center gap-sm">
                {inlineActions.map((action) => {
                  const cls = action === lead ? BTN_PRIMARY : BTN;
                  switch (action) {
                    case "confirm":
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => void confirmWebdoc()}
                          disabled={busy !== null}
                          className={cls}
                        >
                          {busy === "webdoc" ? "Bekräftar…" : "Bekräfta infört"}
                        </button>
                      );
                    case "reply":
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => void sendAndClose()}
                          disabled={busy !== null || !text.trim()}
                          className={cls}
                        >
                          {busy === "send"
                            ? "Skickar…"
                            : coachCheckin
                              ? "Skicka svar och slutför"
                              : "Skicka och avsluta"}
                        </button>
                      );
                    case "escalate":
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => void escalate()}
                          disabled={busy !== null}
                          className={cls}
                        >
                          {busy === "escalate"
                            ? "Skickar…"
                            : "Skicka till läkare"}
                        </button>
                      );
                    case "follow_up":
                      return askingFollowUp ? (
                        <button
                          key={action}
                          type="button"
                          onClick={() => void scheduleFollowUp()}
                          disabled={busy !== null || followUpProblem !== null}
                          className={BTN}
                        >
                          {busy === "follow_up"
                            ? "Sparar…"
                            : "Spara uppföljning"}
                        </button>
                      ) : (
                        <button
                          key={action}
                          type="button"
                          onClick={() => setAskingFollowUp(true)}
                          disabled={busy !== null}
                          className={BTN}
                        >
                          Följ upp senare
                        </button>
                      );
                    case "close":
                      return askingReason ? (
                        <button
                          key={action}
                          type="button"
                          onClick={() => void closeWithoutAction()}
                          disabled={busy !== null || !closeReady}
                          className={BTN}
                        >
                          {busy === "close" ? "Avslutar…" : "Bekräfta avslut"}
                        </button>
                      ) : (
                        <button
                          key={action}
                          type="button"
                          onClick={() => setAskingReason(true)}
                          disabled={busy !== null}
                          className={BTN}
                        >
                          {closeLabel}
                        </button>
                      );
                  }
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// TES-751 — "Behöver återkontakt".
//
// The panel leads with the twelve-week strip because that is the one thing a
// coach cannot reconstruct from anywhere else, and because it answers the
// question the card's title raises — how long, and was she ever here — before
// she has to click anything.
//
// The three statuses at the bottom are decision support, not a verdict.
// Emelie 2026-09-17: active payment or contact with a doctor
// "raderar inte signalen automatiskt, utan visas som beslutsstöd".
// So they are shown next to the absence, never used to suppress it.

const CYCLE_STYLE: Record<
  FollowUpContactContext["history"][number]["outcome"],
  { className: string; label: string }
> = {
  checked_in: { className: "bg-success", label: "Incheckad" },
  milestone_instead: { className: "bg-brandPrimary", label: "Milstolpe" },
  // TES-1526 (Ö-768) — F-04 held back by an open F-05: not a miss.
  held_back: { className: "bg-surfaceMuted/40", label: "F-05 väntar" },
  missed: { className: "bg-surfaceMuted", label: "Utebliven" },
};

function FollowUpContactPanel({
  card,
  patientId,
  busy,
  onPolicy,
}: {
  card: FollowUpContactContext;
  patientId: string;
  busy: Busy;
  onPolicy: (
    body: SetFollowUpPolicyInput,
    busyKey: Extract<Busy, "pause" | "interval">,
  ) => void;
}) {
  return (
    <div className="flex flex-col gap-md rounded-md border border-surfaceMuted p-sm">
      <Section title="Tolv veckor">
        {/* Oldest to newest reads like a calendar; the context arrives newest
            first because that is the order the run is counted in. Reversed in
            JS rather than with flex-row-reverse — the CSS version puts the
            reader one direction-puzzle away from the data. */}
        <div className="flex gap-[3px]">
          {[...card.history].reverse().map((w) => (
            <span
              key={w.weekStart}
              title={`${w.weekStart} — ${CYCLE_STYLE[w.outcome].label}`}
              aria-label={`${w.weekStart}: ${CYCLE_STYLE[w.outcome].label}`}
              className={`h-6 w-4 rounded-sm ${CYCLE_STYLE[w.outcome].className}`}
            />
          ))}
        </div>
        <dl className="mt-sm grid grid-cols-2 gap-x-md gap-y-xs text-sm">
          <dt className="text-textSecondary">Uteblivna i följd</dt>
          <dd className="text-textPrimary">{card.consecutiveMissedCycles}</dd>
          <dt className="text-textSecondary">Senaste incheckning</dt>
          <dd className="text-textPrimary">
            {card.lastCheckInAt ? card.lastCheckInAt.slice(0, 10) : "Aldrig"}
          </dd>
          <dt className="text-textSecondary">Tidigare signaler</dt>
          <dd className="text-textPrimary">{card.priorSignalCount}</dd>
        </dl>
      </Section>

      <Section title="Tidigare coachkontakt">
        {card.priorContacts.length === 0 ? (
          <Empty>Ingen tidigare kontakt registrerad.</Empty>
        ) : (
          <Bullets
            items={card.priorContacts.map((c) => ({
              id: c.handledAt,
              // TES-1208 — a close without contact says why.
              body: [
                DISPOSITION_LABEL[c.disposition] ?? c.disposition,
                c.reasonCode
                  ? closeReasonLabel(c.reasonCode, "needs_follow_up_contact")
                  : null,
                c.reason || null,
              ]
                .filter(Boolean)
                .join(" · "),
              meta: c.handledAt.slice(0, 10),
            }))}
          />
        )}
      </Section>

      <Section title="Påminnelser">
        {card.policy?.pausedUntil ? (
          <p className="mb-xs text-sm text-textSecondary">
            Pausad till {card.policy.pausedUntil.slice(0, 10)}.
          </p>
        ) : null}
        {card.policy?.contactIntervalWeeks ? (
          <p className="mb-xs text-sm text-textSecondary">
            Överenskommen kontakt var {card.policy.contactIntervalWeeks}:e
            vecka.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-xs">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => onPolicy({ pauseWeeks: 4 }, "pause")}
            className="rounded-md border border-surfaceMuted px-sm py-xs text-sm disabled:opacity-50"
          >
            {card.policy?.pausedUntil ? "Förläng pausen" : "Pausa 4 veckor"}
          </button>
          {card.policy?.pausedUntil ? (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => onPolicy({ pauseWeeks: 0 }, "pause")}
              className="rounded-md border border-surfaceMuted px-sm py-xs text-sm disabled:opacity-50"
            >
              Häv pausen
            </button>
          ) : null}
          <label className="flex items-center gap-xs text-sm text-textSecondary">
            Kontakt var
            <select
              id={`follow-up-interval-${patientId}`}
              disabled={busy !== null}
              value={card.policy?.contactIntervalWeeks ?? ""}
              onChange={(e) =>
                onPolicy(
                  {
                    contactIntervalWeeks: e.target.value
                      ? Number(e.target.value)
                      : null,
                  },
                  "interval",
                )
              }
              className="rounded-md border border-surfaceMuted bg-surface px-xs py-[2px] text-sm"
            >
              <option value="">standard</option>
              {[2, 3, 4, 6, 8, 12].map((w) => (
                <option key={w} value={w}>
                  {w}:e vecka
                </option>
              ))}
            </select>
          </label>
        </div>
      </Section>

      {/* TES-1333 — A-01's order: what happened and what to do before the
          technical status. The three statuses are decision support (Emelie
          2026-09-17), so they stay, last and low-key. */}
      <Section title="Status">
        <ul className="flex flex-wrap gap-xs">
          <StatusChip
            on={card.hasActiveCareRelationship}
            label="Vårdrelation"
          />
          <StatusChip on={card.hasActiveSubscription} label="Abonnemang" />
          <StatusChip
            on={card.medicalFollowUpInProgress}
            label="Medicinsk uppföljning"
          />
        </ul>
      </Section>
    </div>
  );
}

function StatusChip({ on, label }: { on: boolean; label: string }) {
  return (
    <li
      className={`rounded-full px-sm py-[2px] text-xs ${
        on
          ? "bg-success/15 text-textPrimary"
          : "bg-surfaceMuted text-textSecondary"
      }`}
    >
      {label}: {on ? "aktiv" : "ingen"}
    </li>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-textSecondary">{children}</p>;
}

function Bullets({
  items,
}: {
  items: Array<{ id: string; body: string; meta: string }>;
}) {
  return (
    <ul className="flex flex-col gap-xs">
      {items.map((i) => (
        <li key={i.id} className="text-sm text-textPrimary">
          {i.body}{" "}
          <span className="text-xs text-textSecondary">({i.meta})</span>
        </li>
      ))}
    </ul>
  );
}
